<template>
    <div class="aes-page">
        <!-- Header -->
        <div class="aes-header">
            <div class="aes-header-left">
                <div class="aes-icon-wrap"><i class="fas fa-users"></i></div>
                <h2 class="aes-title">Employee Asset Summary</h2>
                <span class="aes-count" v-if="!loading">{{ filteredRows.length }}</span>
            </div>
            <div class="aes-header-right">
                <div class="aes-search-wrap">
                    <i class="fas fa-search aes-search-icon"></i>
                    <input class="aes-search" v-model="search" placeholder="Search employee..." />
                    <button class="aes-search-clear" v-if="search" @click="search = ''">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <div class="aes-toggle-group">
                    <button class="aes-toggle" :class="{ active: statusFilter === 'all' }" @click="statusFilter = 'all'">All</button>
                    <button class="aes-toggle" :class="{ active: statusFilter === 'allocated' }" @click="statusFilter = 'allocated'">Has Assets</button>
                    <button class="aes-toggle" :class="{ active: statusFilter === 'none' }" @click="statusFilter = 'none'">No Assets</button>
                </div>
            </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="aes-loading">
            <div class="aes-spinner"></div>
            <span>Loading...</span>
        </div>

        <!-- Empty -->
        <div v-else-if="!filteredRows.length" class="aes-empty">
            <div class="aes-empty-icon"><i class="fas fa-users-slash"></i></div>
            <h3>{{ search ? 'No employees match your search' : 'No employee data found' }}</h3>
        </div>

        <!-- Table -->
        <div v-else class="aes-table-wrap">
            <table class="aes-table">
                <thead>
                    <tr>
                        <th class="th-idx">#</th>
                        <th>Employee</th>
                        <th>Department</th>
                        <th>Designation</th>
                        <th class="text-center th-num">
                            <span class="th-badge th-allocated"><i class="fas fa-check-circle"></i> Allocated</span>
                        </th>
                        <th class="text-center th-num">
                            <span class="th-badge th-returned"><i class="fas fa-undo-alt"></i> Returned</span>
                        </th>
                        <th class="text-center th-num">Total</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, idx) in paginatedRows" :key="row.employeeId">
                        <td class="td-idx">{{ (currentPage - 1) * pageSize + idx + 1 }}</td>
                        <td>
                            <div class="td-emp">
                                <div class="td-emp-avatar" :class="empColor(idx)">
                                    {{ initials(row.employeeName) }}
                                </div>
                                <div class="td-emp-info">
                                    <span class="td-emp-name">{{ row.employeeName }}</span>
                                    <span class="td-emp-id" v-if="row.employeeCode">{{ row.employeeCode }}</span>
                                </div>
                            </div>
                        </td>
                        <td class="td-muted">{{ row.department || '—' }}</td>
                        <td class="td-muted">{{ row.designation || '—' }}</td>
                        <td class="text-center">
                            <span class="aes-pill aes-pill-allocated" v-if="row.allocated > 0">{{ row.allocated }}</span>
                            <span class="td-zero" v-else>0</span>
                        </td>
                        <td class="text-center">
                            <span class="aes-pill aes-pill-returned" v-if="row.returned > 0">{{ row.returned }}</span>
                            <span class="td-zero" v-else>0</span>
                        </td>
                        <td class="text-center">
                            <span class="aes-pill aes-pill-total" v-if="row.total > 0">{{ row.total }}</span>
                            <span class="td-zero" v-else>0</span>
                        </td>
                    </tr>
                </tbody>
                <!-- Summary row -->
                <tfoot>
                    <tr class="aes-total-row">
                        <td colspan="4" class="aes-total-label">Total ({{ filteredRows.length }} employees)</td>
                        <td class="text-center"><strong>{{ totalAllocated }}</strong></td>
                        <td class="text-center"><strong>{{ totalReturned }}</strong></td>
                        <td class="text-center"><strong>{{ totalAllocated + totalReturned }}</strong></td>
                    </tr>
                </tfoot>
            </table>

            <!-- Pagination -->
            <div class="aes-pagination" v-if="totalPages > 1">
                <button class="aes-page-btn" :disabled="currentPage === 1" @click="currentPage--">
                    <i class="fas fa-chevron-left"></i>
                </button>
                <button
                    v-for="p in visiblePages" :key="p"
                    class="aes-page-btn"
                    :class="{ 'aes-page-active': p === currentPage }"
                    @click="currentPage = p"
                >{{ p }}</button>
                <button class="aes-page-btn" :disabled="currentPage === totalPages" @click="currentPage++">
                    <i class="fas fa-chevron-right"></i>
                </button>
                <span class="aes-page-info">Page {{ currentPage }} of {{ totalPages }} &nbsp;·&nbsp; {{ filteredRows.length }} records</span>
            </div>
        </div>
    </div>
</template>

<script>
import { http } from "../../utils/http"

export default {
    data() {
        return {
            loading: true,
            allocations: [],
            employees: [],
            search: "",
            statusFilter: "all",
            currentPage: 1,
            pageSize: 10,
        }
    },
    mounted() { this.init() },
    computed: {
        rows() {
            // Build a map: employeeId → { meta, allocated, returned }
            const map = {}

            // Seed with all employees so those with 0 assets still appear
            for (const emp of this.employees) {
                const id = String(emp.id)
                map[id] = {
                    employeeId: id,
                    employeeName: `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || "—",
                    employeeCode: emp.employeeCode || emp.code || "",
                    department: emp.department?.name || emp.departmentName || emp.department || "",
                    designation: emp.designation?.name || emp.designationName || emp.designation || "",
                    allocated: 0,
                    returned: 0,
                    total: 0,
                }
            }

            // Count allocations
            for (const item of this.allocations) {
                const id = String(item.employeeId || (item.employee && item.employee.id) || "")
                if (!id) continue
                if (!map[id]) {
                    // Employee not in masters but has an allocation
                    map[id] = {
                        employeeId: id,
                        employeeName: item.employeeName || this.empName(item.employee),
                        employeeCode: "",
                        department: "",
                        designation: "",
                        allocated: 0,
                        returned: 0,
                        total: 0,
                    }
                }
                const isReturned = (item.statusName || "").toLowerCase().includes("return") || item.returned === true
                if (isReturned) {
                    map[id].returned++
                } else {
                    map[id].allocated++
                }
                map[id].total++
            }

            return Object.values(map).sort((a, b) => b.total - a.total || a.employeeName.localeCompare(b.employeeName))
        },
        filteredRows() {
            let rows = this.rows
            if (this.search) {
                const q = this.search.toLowerCase()
                rows = rows.filter(r =>
                    r.employeeName.toLowerCase().includes(q) ||
                    (r.employeeCode && r.employeeCode.toLowerCase().includes(q)) ||
                    (r.department && r.department.toLowerCase().includes(q)) ||
                    (r.designation && r.designation.toLowerCase().includes(q))
                )
            }
            if (this.statusFilter === "allocated") rows = rows.filter(r => r.allocated > 0)
            if (this.statusFilter === "none")      rows = rows.filter(r => r.allocated === 0)
            return rows
        },
        paginatedRows() {
            const start = (this.currentPage - 1) * this.pageSize
            return this.filteredRows.slice(start, start + this.pageSize)
        },
        totalPages() { return Math.ceil(this.filteredRows.length / this.pageSize) },
        visiblePages() {
            const pages = []
            const start = Math.max(1, this.currentPage - 2)
            const end   = Math.min(this.totalPages, this.currentPage + 2)
            for (let i = start; i <= end; i++) pages.push(i)
            return pages
        },
        totalAllocated() { return this.filteredRows.reduce((s, r) => s + r.allocated, 0) },
        totalReturned()  { return this.filteredRows.reduce((s, r) => s + r.returned,  0) },
    },
    watch: {
        search()       { this.currentPage = 1 },
        statusFilter() { this.currentPage = 1 },
    },
    methods: {
        initials(name) {
            return (name || "?").split(" ").slice(0, 2).map(w => w[0] || "").join("").toUpperCase()
        },
        empColor(i) {
            return ["aes-av-blue","aes-av-purple","aes-av-teal","aes-av-amber","aes-av-rose"][i % 5]
        },
        empName(emp) {
            if (!emp) return "—"
            return `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || "—"
        },
        async init() {
            this.loading = true
            try {
                const [allocRes, masterRes] = await Promise.all([
                    http.get("/asset-allocations"),
                    http.get("/asset-allocations/masters"),
                ])
                this.allocations = allocRes.data.result || []
                this.employees   = masterRes.data.result?.employees || []
            } catch (e) {
                console.error("asset-employee-summary init error", e)
            }
            this.loading = false
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.aes-page { animation: aesFade .3s ease; }
@keyframes aesFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:none; } }

/* ── Header ── */
.aes-header {
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; background: white; border: 1px solid $gray100;
    border-radius: 8px; padding: 10px 16px; margin-bottom: 12px;
    box-shadow: $shadowSm; flex-wrap: wrap;
}
.aes-header-left  { display:flex; align-items:center; gap:10px; }
.aes-header-right { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.aes-icon-wrap {
    width:32px; height:32px; border-radius:8px;
    background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%));
    color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px;
}
.aes-title { font-size:15px; font-weight:700; color:$gray800; margin:0; }
.aes-count {
    min-width:20px; height:20px; padding:0 6px; border-radius:$radiusFull;
    font-size:11px; font-weight:700; background:$primaryColor; color:white;
    display:inline-flex; align-items:center; justify-content:center;
}

/* ── Search ── */
.aes-search-wrap {
    display:flex; align-items:center; gap:7px;
    border:1px solid $gray200; border-radius:7px;
    padding:5px 10px; background:white; transition:border-color .15s;
    &:focus-within { border-color:$primaryColor; box-shadow:0 0 0 3px rgba($primaryColor,.08); }
}
.aes-search-icon { font-size:11px; color:$gray400; flex-shrink:0; }
.aes-search {
    border:none; outline:none; font-size:12.5px; font-family:inherit;
    color:$gray700; background:transparent; width:180px;
    &::placeholder { color:$gray400; }
}
.aes-search-clear {
    border:none; background:none; cursor:pointer; color:$gray400; padding:0; font-size:10px;
    &:hover { color:$gray600; }
}

/* ── Toggle group ── */
.aes-toggle-group { display:flex; gap:3px; background:$gray50; border:1px solid $gray100; border-radius:6px; padding:3px; }
.aes-toggle {
    padding:4px 12px; border:none; border-radius:5px; font-size:11.5px; font-weight:600;
    cursor:pointer; background:transparent; color:$gray500; transition:all $transitionFast; font-family:inherit;
    &:hover { color:$gray700; }
    &.active { background:white; color:$primaryColor; box-shadow:$shadowSm; }
}

/* ── Loading / Empty ── */
.aes-loading {
    display:flex; align-items:center; justify-content:center; gap:10px;
    padding:64px 16px; background:white; border:1px solid $gray100;
    border-radius:8px; box-shadow:$shadowSm;
    span { font-size:13px; color:$gray400; }
}
.aes-spinner {
    width:24px; height:24px; border:2.5px solid $gray200;
    border-top-color:$primaryColor; border-radius:50%; animation:aesSpin .7s linear infinite;
}
@keyframes aesSpin { to { transform:rotate(360deg); } }
.aes-empty {
    text-align:center; padding:64px 16px; background:white;
    border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm;
    h3 { font-size:14px; font-weight:700; color:$gray600; margin:0; }
}
.aes-empty-icon {
    width:48px; height:48px; border-radius:$radiusFull; background:$gray50;
    border:1px solid $gray100; display:flex; align-items:center; justify-content:center;
    margin:0 auto 12px;
    i { font-size:20px; color:$gray300; }
}

/* ── Table ── */
.aes-table-wrap { background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; overflow:hidden; }
.aes-table { width:100%; border-collapse:collapse; }
.aes-table thead { background:$gray50; border-bottom:2px solid $gray100; }
.aes-table th {
    padding:10px 14px; font-size:11px; font-weight:600; color:$gray500;
    text-transform:uppercase; letter-spacing:0.3px; white-space:nowrap;
}
.aes-table td {
    padding:10px 14px; font-size:12.5px; color:$gray700; font-weight:500;
    border-bottom:1px solid $gray100; vertical-align:middle;
}
.aes-table tbody tr {
    transition:background $transitionFast;
    &:last-child td { border-bottom:none; }
    &:hover { background:$gray50; }
}
.th-idx, .td-idx { width:36px; text-align:center; color:$gray400; font-size:11px; }
.text-center { text-align:center; }
.th-num { width:110px; }
.td-muted { color:$gray500; font-size:12px; }
.td-zero { color:$gray300; font-size:12px; font-weight:500; }

/* column header badges */
.th-badge {
    display:inline-flex; align-items:center; gap:4px; padding:2px 8px;
    border-radius:$radiusFull; font-size:10px; font-weight:700; letter-spacing:0.2px;
    i { font-size:9px; }
}
.th-allocated { background:#f0fdf4; color:#16a34a; }
.th-returned  { background:$gray100;  color:$gray500; }

/* ── Employee cell ── */
.td-emp { display:flex; align-items:center; gap:10px; }
.td-emp-avatar {
    width:32px; height:32px; border-radius:$radiusFull;
    display:inline-flex; align-items:center; justify-content:center;
    font-size:11px; font-weight:700; flex-shrink:0; letter-spacing:0.5px;
}
.aes-av-blue   { background:#eff6ff; color:#2563eb; }
.aes-av-purple { background:#f5f3ff; color:#7c3aed; }
.aes-av-teal   { background:#f0fdfa; color:#0d9488; }
.aes-av-amber  { background:#fffbeb; color:#d97706; }
.aes-av-rose   { background:#fff1f2; color:#e11d48; }
.td-emp-info { display:flex; flex-direction:column; gap:1px; }
.td-emp-name { font-size:13px; font-weight:600; color:$gray800; }
.td-emp-id   { font-size:10.5px; color:$gray400; font-weight:500; }

/* ── Count pills ── */
.aes-pill {
    display:inline-flex; align-items:center; justify-content:center;
    min-width:28px; height:22px; padding:0 8px; border-radius:$radiusFull;
    font-size:11.5px; font-weight:700;
}
.aes-pill-allocated { background:#f0fdf4; color:#16a34a; }
.aes-pill-returned  { background:$gray100;  color:$gray500; }
.aes-pill-total     { background:$primaryLight; color:$primaryColor; }

/* ── Footer total row ── */
.aes-total-row {
    background:$gray50; border-top:2px solid $gray200;
    td { font-size:12px; color:$gray600; padding:9px 14px; border-bottom:none; }
}
.aes-total-label { font-weight:600; color:$gray500; font-size:11.5px; }

/* ── Pagination ── */
.aes-pagination {
    display:flex; align-items:center; gap:4px; padding:12px 16px;
    border-top:1px solid $gray100; flex-wrap:wrap;
}
.aes-page-btn {
    width:30px; height:30px; border:1px solid $gray200; border-radius:6px;
    background:white; color:$gray600; font-size:11.5px; font-weight:600;
    cursor:pointer; display:inline-flex; align-items:center; justify-content:center;
    transition:all $transitionFast; font-family:inherit;
    &:hover:not(:disabled) { background:$gray50; border-color:$gray300; }
    &:disabled { opacity:0.35; cursor:not-allowed; }
}
.aes-page-active { background:$primaryColor !important; color:white !important; border-color:$primaryColor !important; }
.aes-page-info { margin-left:8px; font-size:11.5px; color:$gray400; font-weight:500; }

/* ── Responsive ── */
@media(max-width:1200px) { .aes-table-wrap { overflow-x:auto; } }
@media(max-width:768px) {
    .aes-header { flex-direction:column; align-items:stretch; }
    .aes-header-right { flex-direction:column; align-items:flex-start; }
    .aes-search { width:100%; }
}
</style>
